import type { Voice } from './audioBus';

/**
 * The synthesis toolkit every sound is built from: envelopes, tones with sweeps, vibrato, FM and
 * tremolo, filtered noise, and a cheap room reverb. Nothing here allocates more than a handful
 * of nodes per sound, which is what keeps a duel affordable on a Quest 2.
 */
export type NoiseKind = 'white' | 'pink';

export interface Envelope {
  /** Seconds to reach the peak. */
  attack?: number;
  /** Seconds at the peak before the decay. */
  hold?: number;
  /** Seconds from the peak down to the sustain level. */
  decay?: number;
  /** Level held after the decay, as a fraction of the peak. */
  sustain?: number;
  /** Seconds from the end of the note to silence. */
  release?: number;
}

export interface Sweep {
  /** Where the frequency ends up, Hz. */
  to: number;
  /** Seconds the sweep takes; the whole note by default. */
  seconds?: number;
}

export interface FilterSpec {
  type: BiquadFilterType;
  freq: number;
  sweep?: Sweep;
  q?: number;
  gain?: number;
}

interface SoundSpec {
  /** Seconds the note is held before its release. */
  seconds: number;
  env?: Envelope;
  /** Peak level, 0 … 1. */
  gain?: number;
  /** Start offset from the voice's `now`, seconds. */
  at?: number;
  filter?: FilterSpec;
  /** Amplitude modulation (tremolo, roughness): rate in Hz and depth 0 … 1. */
  am?: { hz: number; depth: number };
  /** Where to connect; the voice input by default. */
  out?: AudioNode;
}

export interface ToneSpec extends SoundSpec {
  type?: OscillatorType;
  freq: number;
  sweep?: Sweep;
  /** Pitch wobble: rate in Hz and depth in cents. */
  vibrato?: { hz: number; cents: number };
  /** Frequency modulation by a sine at `ratio` × the pitch, `depth` Hz deep, fading to `depthTo`. */
  fm?: { ratio: number; depth: number; depthTo?: number };
}

export interface NoiseSpec extends SoundSpec {
  kind?: NoiseKind;
  /** Playback speed; below 1 darkens the noise. */
  rate?: number;
}

const MIN_HZ = 10;

/** A frequency the context can represent (exponential ramps must never reach zero). */
export function hz(ctx: BaseAudioContext, value: number): number {
  return Math.min(ctx.sampleRate / 2 - 1, Math.max(MIN_HZ, value));
}

/** Shapes a gain parameter as attack – hold – decay – sustain – release; returns when it is silent. */
export function envelope(
  param: AudioParam,
  t0: number,
  seconds: number,
  env: Envelope = {},
  peak = 1,
): number {
  const attack = env.attack ?? 0.005;
  const hold = env.hold ?? 0;
  const decay = env.decay ?? 0;
  const sustain = env.sustain ?? 1;
  const release = env.release ?? 0.05;
  param.setValueAtTime(0, t0);
  param.linearRampToValueAtTime(peak, t0 + attack);
  let level = peak;
  if (decay > 0) {
    param.setValueAtTime(peak, t0 + attack + hold);
    param.linearRampToValueAtTime(peak * sustain, t0 + attack + hold + decay);
    level = peak * sustain;
  }
  const releaseAt = Math.max(t0 + attack + hold + decay, t0 + seconds);
  param.setValueAtTime(level, releaseAt);
  param.linearRampToValueAtTime(0, releaseAt + release);
  return releaseAt + release;
}

/** An oscillator note; returns the time it falls silent. */
export function tone(v: Voice, spec: ToneSpec): number {
  const ctx = v.context;
  const t0 = v.now + (spec.at ?? 0);
  const amp = ctx.createGain();
  const end = envelope(amp.gain, t0, spec.seconds, spec.env, spec.gain ?? 0.5);
  const osc = v.track(ctx.createOscillator());
  osc.type = spec.type ?? 'sine';
  const from = hz(ctx, spec.freq);
  osc.frequency.setValueAtTime(from, t0);
  if (spec.sweep)
    osc.frequency.exponentialRampToValueAtTime(
      hz(ctx, spec.sweep.to),
      t0 + (spec.sweep.seconds ?? spec.seconds),
    );
  if (spec.vibrato) {
    const lfo = v.track(ctx.createOscillator());
    lfo.frequency.value = spec.vibrato.hz;
    const depth = ctx.createGain();
    depth.gain.value = spec.vibrato.cents;
    lfo.connect(depth);
    depth.connect(osc.detune);
    lfo.start(t0);
    lfo.stop(end);
  }
  if (spec.fm) {
    const mod = v.track(ctx.createOscillator());
    mod.frequency.setValueAtTime(hz(ctx, from * spec.fm.ratio), t0);
    if (spec.sweep)
      mod.frequency.exponentialRampToValueAtTime(
        hz(ctx, spec.sweep.to * spec.fm.ratio),
        t0 + (spec.sweep.seconds ?? spec.seconds),
      );
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(spec.fm.depth, t0);
    if (spec.fm.depthTo !== undefined) depth.gain.linearRampToValueAtTime(spec.fm.depthTo, end);
    mod.connect(depth);
    depth.connect(osc.frequency);
    mod.start(t0);
    mod.stop(end);
  }
  chain(v, osc, amp, spec, t0, end);
  osc.start(t0);
  osc.stop(end);
  return end;
}

/** A burst of (filtered) noise; returns the time it falls silent. */
export function noise(v: Voice, spec: NoiseSpec): number {
  const ctx = v.context;
  const t0 = v.now + (spec.at ?? 0);
  const amp = ctx.createGain();
  const end = envelope(amp.gain, t0, spec.seconds, spec.env, spec.gain ?? 0.5);
  const source = v.track(ctx.createBufferSource());
  const buffer = v.noise(spec.kind ?? 'white');
  source.buffer = buffer;
  source.loop = true;
  source.playbackRate.value = spec.rate ?? 1;
  chain(v, source, amp, spec, t0, end);
  // A random start point, so two bursts never crackle identically.
  source.start(t0, Math.random() * Math.max(0, buffer.duration - 0.5));
  source.stop(end);
  return end;
}

/** source → [filter] → [tremolo] → amp → out */
function chain(
  v: Voice,
  source: AudioNode,
  amp: GainNode,
  spec: SoundSpec,
  t0: number,
  end: number,
): void {
  let node = source;
  if (spec.filter) {
    const f = filter(v.context, spec.filter, t0, spec.seconds);
    node.connect(f);
    node = f;
  }
  if (spec.am) {
    const t = tremolo(v, spec.am, t0, end);
    node.connect(t);
    node = t;
  }
  node.connect(amp);
  amp.connect(spec.out ?? v.input);
}

export function filter(
  ctx: BaseAudioContext,
  spec: FilterSpec,
  t0: number,
  seconds: number,
): BiquadFilterNode {
  const f = ctx.createBiquadFilter();
  f.type = spec.type;
  f.frequency.setValueAtTime(hz(ctx, spec.freq), t0);
  if (spec.sweep)
    f.frequency.exponentialRampToValueAtTime(
      hz(ctx, spec.sweep.to),
      t0 + (spec.sweep.seconds ?? seconds),
    );
  f.Q.value = spec.q ?? 1;
  if (spec.gain !== undefined) f.gain.value = spec.gain;
  return f;
}

/** A gain that wobbles between 1 − 2·depth and 1 at `hz`. */
export function tremolo(
  v: Voice,
  am: { hz: number; depth: number },
  t0: number,
  end: number,
): GainNode {
  const ctx = v.context;
  const trem = ctx.createGain();
  trem.gain.value = 1 - am.depth;
  const lfo = v.track(ctx.createOscillator());
  lfo.frequency.value = am.hz;
  const depth = ctx.createGain();
  depth.gain.value = am.depth;
  lfo.connect(depth);
  depth.connect(trem.gain);
  lfo.start(t0);
  lfo.stop(end);
  return trem;
}

export function createNoiseBuffer(
  ctx: BaseAudioContext,
  kind: NoiseKind,
  seconds: number,
): AudioBuffer {
  const length = Math.max(1, Math.floor(seconds * ctx.sampleRate));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  if (kind === 'white') {
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }
  // Pink noise by Paul Kellet's economy filter: three leaky integrators of white noise.
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99765 * b0 + white * 0.099046;
    b1 = 0.963 * b1 + white * 0.2965164;
    b2 = 0.57 * b2 + white * 1.0526913;
    data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.22;
  }
  return buffer;
}

/**
 * A small room: three feedback delay lines of unrelated lengths, each darkened a little on every
 * pass. Far cheaper than a convolution and enough to set stings and chimes in a space.
 */
export function createReverb(ctx: BaseAudioContext, out: AudioNode): { input: AudioNode } {
  const input = ctx.createGain();
  const wet = ctx.createGain();
  wet.gain.value = 0.35;
  wet.connect(out);
  for (const seconds of [0.047, 0.071, 0.097]) {
    const delay = ctx.createDelay(0.5);
    delay.delayTime.value = seconds;
    const damp = ctx.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 2600;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.55;
    input.connect(delay);
    delay.connect(damp);
    damp.connect(feedback);
    feedback.connect(delay);
    damp.connect(wet);
  }
  return { input };
}

export const rand = (min: number, max: number): number => min + Math.random() * (max - min);

export const pick = <T>(items: readonly T[]): T => items[Math.floor(Math.random() * items.length)]!;

/** `value` varied by up to ±`amount` of itself. */
export const jitter = (value: number, amount: number): number =>
  value * (1 + rand(-amount, amount));

/** MIDI note number → Hz (69 = A4 = 440 Hz). */
export const midi = (note: number): number => 440 * 2 ** ((note - 69) / 12);
